import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  Download,
  FileCopy,
  ContentCopy,
  CheckCircle,
} from '@mui/icons-material'

interface FileInfo {
  contentType: string
  size: number
  name: string
  url: string
}

export default function FileView() {
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    fetchFileInfo()
  }, [id])

  const fetchFileInfo = async () => {
    try {
      const response = await fetch(`/api/file/${id}`)
      if (!response.ok) {
        throw new Error('文件不存在或已过期')
      }

      const contentType = response.headers.get('Content-Type') || 'application/octet-stream'
      const size = parseInt(response.headers.get('Content-Length') || '0', 10)
      
      setFileInfo({
        contentType,
        size,
        name: id || '未知文件',
        url: `/api/file/${id}`,
      })
    } catch (error) {
      setError(error instanceof Error ? error.message : '加载失败')
    } finally {
      setLoading(false)
    }
  }

  const handleCopyLink = async () => {
    const shareUrl = window.location.href
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('复制失败:', error)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  const renderPreview = () => {
    if (!fileInfo) return null

    if (fileInfo.contentType.startsWith('image/')) {
      return (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <img
            src={fileInfo.url}
            alt={fileInfo.name}
            style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
          />
        </Box>
      )
    }

    if (fileInfo.contentType.startsWith('text/') || 
        fileInfo.contentType === 'application/json') {
      return (
        <Box sx={{ mt: 2 }}>
          <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: '500px', overflow: 'auto' }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {/* 文本内容将在组件加载时获取 */}
              正在加载文本内容...
            </pre>
          </Paper>
        </Box>
      )
    }

    if (fileInfo.contentType === 'application/pdf') {
      return (
        <Box sx={{ mt: 2, height: '500px' }}>
          <iframe
            src={`${fileInfo.url}#view=FitH`}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        </Box>
      )
    }

    return (
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          此文件类型暂不支持预览，请下载后查看
        </Typography>
      </Box>
    )
  }

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FileCopy sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6" component="h1">
              {fileInfo?.name}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              文件大小：{formatFileSize(fileInfo?.size || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              文件类型：{fileInfo?.contentType}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<Download />}
              href={`${fileInfo?.url}?download=true`}
            >
              下载文件
            </Button>
            <Button
              variant="outlined"
              startIcon={copied ? <CheckCircle /> : <ContentCopy />}
              onClick={handleCopyLink}
            >
              {copied ? '已复制' : '复制链接'}
            </Button>
          </Box>

          {renderPreview()}
        </Paper>
      </Box>
    </Container>
  )
} 